/**
 * @file Shared phone country codes — imported by both web and mobile.
 *
 * Each entry has: dial code, flag emoji, and country name in Spanish.
 * Covers Spanish-speaking LATAM countries + Spain.
 *
 * Usage:
 *   import { COUNTRY_CODES } from '../../../shared/country-codes';  // web
 *   import { COUNTRY_CODES } from '../../shared/country-codes';     // mobile
 */

export const COUNTRY_CODES = [
  { code: "+52", flag: "🇲🇽", label: "México" },
  { code: "+57", flag: "🇨🇴", label: "Colombia" },
  { code: "+54", flag: "🇦🇷", label: "Argentina" },
  { code: "+56", flag: "🇨🇱", label: "Chile" },
  { code: "+51", flag: "🇵🇪", label: "Perú" },
  { code: "+593", flag: "🇪🇨", label: "Ecuador" },
  { code: "+591", flag: "🇧🇴", label: "Bolivia" },
  { code: "+595", flag: "🇵🇾", label: "Paraguay" },
  { code: "+598", flag: "🇺🇾", label: "Uruguay" },
  { code: "+506", flag: "🇨🇷", label: "Costa Rica" },
  { code: "+507", flag: "🇵🇦", label: "Panamá" },
  { code: "+502", flag: "🇬🇹", label: "Guatemala" },
  { code: "+503", flag: "🇸🇻", label: "El Salvador" },
  { code: "+504", flag: "🇭🇳", label: "Honduras" },
  { code: "+1809", flag: "🇩🇴", label: "Rep. Dominicana" },
  { code: "+34", flag: "🇪🇸", label: "España" },
];

/**
 * Parse a full phone string to extract country code and local number.
 * Tries longest codes first to avoid partial matches (e.g., +593 vs +5).
 * Defaults to Mexico (+52) if no match.
 */
/**
 * Mexico 2-digit area codes (major cities).
 * Everything else in MX is a 3-digit area code.
 */
const MX_2_DIGIT_AREAS = [
  "55", "33", "81", // CDMX, Guadalajara, Monterrey
  "56", "22", "44", "47", "77", "99", // Toluca metro, Puebla, etc.
];

/**
 * Format a local phone number with spaces for display.
 * The raw digits are unchanged — this is purely visual.
 *
 * @param {string} countryCode - e.g. "+52"
 * @param {string} digits - raw digits only, no country code
 * @returns {string} formatted string with spaces
 */
export const formatLocalNumber = (countryCode, digits) => {
  if (!digits) return "";

  // Mexico: 10 digits — XX XXXX XXXX or XXX XXX XXXX
  if (countryCode === "+52") {
    const prefix2 = digits.slice(0, 2);
    if (MX_2_DIGIT_AREAS.includes(prefix2)) {
      // 2-4-4
      const parts = [digits.slice(0, 2), digits.slice(2, 6), digits.slice(6, 10)];
      return parts.filter(Boolean).join(" ");
    }
    // 3-3-4
    const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)];
    return parts.filter(Boolean).join(" ");
  }

  // Colombia: 10 digits — XXX XXX XXXX
  if (countryCode === "+57") {
    const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)];
    return parts.filter(Boolean).join(" ");
  }

  // Argentina: 10 digits — XX XXXX XXXX
  if (countryCode === "+54") {
    const parts = [digits.slice(0, 2), digits.slice(2, 6), digits.slice(6, 10)];
    return parts.filter(Boolean).join(" ");
  }

  // Chile, Peru: 9 digits — X XXXX XXXX
  if (countryCode === "+56" || countryCode === "+51") {
    const parts = [digits.slice(0, 1), digits.slice(1, 5), digits.slice(5, 9)];
    return parts.filter(Boolean).join(" ");
  }

  // Spain: 9 digits — XXX XXX XXX
  if (countryCode === "+34") {
    const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)];
    return parts.filter(Boolean).join(" ");
  }

  // Central America (8 digits): XXXX XXXX
  if (["+502", "+503", "+504", "+506", "+507"].includes(countryCode)) {
    const parts = [digits.slice(0, 4), digits.slice(4, 8)];
    return parts.filter(Boolean).join(" ");
  }

  // Default: groups of 3-3-4
  const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)];
  return parts.filter(Boolean).join(" ");
};

export const parsePhone = (value) => {
  if (!value) return { countryCode: "+52", localNumber: "" };

  const sorted = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const cc of sorted) {
    if (value.startsWith(cc.code)) {
      return { countryCode: cc.code, localNumber: value.slice(cc.code.length) };
    }
  }

  if (value.startsWith("+")) {
    return { countryCode: "+52", localNumber: value.replace(/^\+\d+/, "") };
  }

  return { countryCode: "+52", localNumber: value };
};
